import { DatePicker, Input, Select } from "antd";
import { useDeferredValue } from "react";
import { useI18n } from "@/i18n";
import { useUrlFilter } from "@/lib/useUrlFilter";
import { useDateRange } from "./useListPage";

/** Search box kept in the URL; q is what the query sends (trimmed, deferred). */
export function useSearchFilter(key = "q") {
  const [value, set] = useUrlFilter<string>(key, "");
  const q = useDeferredValue(value.trim());
  return { q, query: q ? { q } : {}, value, set };
}

export function SearchFilter({ filter, placeholder, width = 260 }: { filter: ReturnType<typeof useSearchFilter>; placeholder: string; width?: number }) {
  return <Input.Search allowClear placeholder={placeholder} value={filter.value} onChange={(e) => filter.set(e.target.value)} style={{ width }} />;
}

/** Created-at range picker for useDateRange. */
export function DateRangeFilter({ range }: { range: ReturnType<typeof useDateRange> }) {
  const { t } = useI18n();
  return (
    <DatePicker.RangePicker
      value={range.value}
      allowEmpty={[true, true]}
      onChange={(v) => range.set(v ? [v[0], v[1]] : null)}
      placeholder={[t("list.from"), t("list.to")]}
    />
  );
}

/**
 * Status filter kept in the URL. A choice is one status or a group sent as a
 * comma-separated list (e.g. open,receiving for "still open").
 */
export function useStatusFilter<T extends string>(choices: readonly T[], fallback: T, key = "status") {
  const [value, set] = useUrlFilter<T>(key, fallback, choices);
  return { value, set };
}

export function StatusFilter<T extends string>({
  filter,
  options,
  width = 200,
}: {
  filter: { value: T; set: (v: T) => void };
  options: { value: T; label: string }[];
  width?: number;
}) {
  return <Select<T> value={filter.value} onChange={filter.set} options={options} style={{ width }} />;
}
